import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import {
	AppleStoreBadge,
	PlayStoreBadge,
	StoreBadgeGroup,
	DarkMode,
} from '@tempo/core';
import {
	LandingFooter,
	LandingFooterSocialNetworkIcon,
	LandingFooterSocialNetwork,
	LandingFooterBottom,
	LandingFooterLink,
	LandingFooterLinks,
	LandingFooterLinkGroup,
} from '@tempo/landing';
import {FacebookIcon, XIcon, InstagramIcon, YoutubeIcon} from '@tempo/icons';
import {useRouter} from 'next/router';

import config from '@deezer/next/services/config';
import DeezerLogo from '@components/DeezerLogo';
import Select from '@components/Select';
import {SupportedLocales} from '@constants/locales';
import {
	availableLanguages,
	languagesInfo,
} from '@deezer/locales/d4c/metas.json';

type LanguageKey = keyof typeof languagesInfo;
type LanguageInfo = (typeof languagesInfo)[LanguageKey];

type LanguageOption = {
	label: string;
	value: LanguageKey;
};

const DEEZER_JOBS_URL = 'https://www.deezerjobs.com';
const DEEZER_DEVELOPERS_URL = 'https://developers.deezer.com';
const APPLE_STORE_LINK =
	'https://apps.apple.com/app/deezer-for-creators/id1502424629';
const GOOGLE_PLAY_LINK =
	'https://play.google.com/store/apps/details?id=com.deezer.analytics';
const FACEBOOK_LINK = 'https://www.facebook.com/deezerfr';
const TWITTER_LINK = 'https://twitter.com/Deezer';
const YOUTUBE_LINK = 'https://www.youtube.com/deezer';
const INSTAGRAM_LINK = 'https://www.instagram.com/deezer/';

const DEEZER_URL = `https://${config.get('host_website_ssl')}`;

const typedAvailableLanguages = availableLanguages as LanguageKey[];
const fallbackLanguage: LanguageKey = 'en';

const isLanguageKey = (value: string): value is LanguageKey =>
	value in languagesInfo;

const getLanguageInfo = (language: string): LanguageInfo => {
	return isLanguageKey(language)
		? languagesInfo[language]
		: languagesInfo[fallbackLanguage];
};

const Footer = () => {
	const {t, i18n} = useTranslation('d4c');
	const router = useRouter();

	const currentLanguageValue = useMemo<LanguageKey>(() => {
		const language = i18n.language || fallbackLanguage;
		return isLanguageKey(language) ? language : fallbackLanguage;
	}, [i18n.language]);

	const currentLanguageInfo = useMemo<LanguageInfo>(() => {
		return getLanguageInfo(currentLanguageValue);
	}, [currentLanguageValue]);

	const theBackstageLink = useMemo(() => {
		const link = 'https://thebackstage-deezer.com';

		if (router.locale === SupportedLocales.fr) {
			return `${link}/fr`;
		}

		if (router.locale === SupportedLocales.pt_BR) {
			return `${link}/br`;
		}

		return link;
	}, [router.locale]);

	const links = useMemo(() => {
		return {
			deezerLinks: {
				title: 'Deezer',
				links: [
					{
						label: t('analyticsartist_text_plans_d4c-web'),
						href: `${DEEZER_URL}/offers`,
					},
					{
						label: t('footer_title_features_d4c-web'),
						href: `${DEEZER_URL}/features`,
					},
					{
						label: t('footer_title_devices_d4c-web'),
						href: `${DEEZER_URL}/devices`,
					},
					{
						label: t('footer_title_help_d4c-web'),
						href: `${DEEZER_URL}/support`,
					},
					{
						label: t('footer_title_aboutus_d4c-web'),
						href: `${DEEZER_URL}/company`,
					},
					{
						label: t('footer_title_careers_d4c-web'),
						href: DEEZER_JOBS_URL,
					},
				],
			},
			userLinks: {
				title: t('footer_title_explore_d4c-web'),
				links: [
					{
						label: t('footer_title_allchannels_d4c-web'),
						href: `${DEEZER_URL}/channels/explore`,
					},
					{
						label: t('analyticshome_title_charts_d4c-web'),
						href: `${DEEZER_URL}/channels/charts`,
					},
					{
						label: t('footer_title_mostpopularreleases_d4c-web'),
						href: `${DEEZER_URL}/channels/popular`,
					},
					{
						label: t('footer_title_newreleases_d4c-web'),
						href: `${DEEZER_URL}/channels/new`,
					},
					{
						label: 'The Backstage - Deezer Blog',
						href: theBackstageLink,
					},
				],
			},
			aboutUsLinks: {
				title: t('footer_title_whoarewe_d4c-web'),
				links: [
					{
						label: t('footer_title_labelsandartists_d4c-web'),
						href: `${DEEZER_URL}/company/label_artists`,
					},
					{
						label: t('footer_title_developers_d4c-web'),
						href: DEEZER_DEVELOPERS_URL,
					},
					{
						label: t('footer_title_press_d4c-web'),
						href: `${DEEZER_URL}/company/press`,
					},
				],
			},
			legalLinks: {
				title: t('footer_title_legal_d4c-web'),
				links: [
					{
						label: t('footer_title_tcsofuse_d4c-web'),
						href: `/legal/termsconditions`,
					},
					{
						label: t('footer_title_personaldataandcookies_d4c-web'),
						href: `/legal/privacypolicy`,
					},
					{
						label: t('footer_title_contactxemail_d4c-web', {
							sprintf: ['privacy@deezer.com'],
						}),
						href: 'mailto:privacy@deezer.com',
					},
					{
						label: t('footer_title_legalnotice_d4c-web'),
						href: `${DEEZER_URL}/legal/legal`,
					},
				],
			},
		};
	}, [t, theBackstageLink]);

	const options = useMemo<LanguageOption[]>(() => {
		return typedAvailableLanguages.map(lang => ({
			label: languagesInfo[lang].name,
			value: lang,
		}));
	}, []);

	const handleLanguageChange = useCallback(
		(value: string) => {
			if (!isLanguageKey(value) || value === currentLanguageValue) {
				return;
			}

			const {pathname, asPath, query} = router;

			document.cookie = `NEXT_LOCALE=${value}`;
			router.replace({pathname, query}, asPath, {locale: value});
		},
		[currentLanguageValue, router],
	);

	return (
		<DarkMode>
			<LandingFooter
				data-theme="dark"
				color="text.neutral.primary.default"
				bg="background.neutral.primary.default"
				mt={0}
			>
				<StoreBadgeGroup>
					<AppleStoreBadge
						locale={currentLanguageValue}
						href={APPLE_STORE_LINK}
					/>
					<PlayStoreBadge
						locale={currentLanguageValue}
						href={GOOGLE_PLAY_LINK}
					/>
				</StoreBadgeGroup>

				<LandingFooterLinks>
					{Object.values(links).map(group => (
						<LandingFooterLinkGroup key={group.title} label={group.title}>
							{group.links.map(link => (
								<LandingFooterLink key={link.label} href={link.href}>
									{link.label}
								</LandingFooterLink>
							))}
						</LandingFooterLinkGroup>
					))}
				</LandingFooterLinks>

				<LandingFooterBottom
					socialIcons={
						<LandingFooterSocialNetwork>
							<LandingFooterSocialNetworkIcon
								aria-label="facebook"
								href={FACEBOOK_LINK}
								icon={<FacebookIcon />}
							/>
							<LandingFooterSocialNetworkIcon
								aria-label="instagram"
								href={INSTAGRAM_LINK}
								icon={<InstagramIcon />}
							/>
							<LandingFooterSocialNetworkIcon
								aria-label="youtube"
								href={YOUTUBE_LINK}
								icon={<YoutubeIcon />}
							/>
							<LandingFooterSocialNetworkIcon
								aria-label="twitter"
								href={TWITTER_LINK}
								icon={<XIcon />}
							/>
						</LandingFooterSocialNetwork>
					}
					languageSelector={
						<Select
							value={currentLanguageValue}
							options={options}
							onChange={handleLanguageChange}
							placeholder={currentLanguageInfo.name}
						/>
					}
					logo={<DeezerLogo />}
				/>
			</LandingFooter>
		</DarkMode>
	);
};

export default React.memo(Footer);
